import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { COUNTRIES, DEFAULT_COUNTRY, getCountry, isCountryActive } from "./countries";

const STORAGE_KEY = "fodindin:country";
const CountryContext = createContext(null);

/** Reads the persisted country code, falling back to DEFAULT_COUNTRY. */
function readStoredCountry() {
  try {
    const code = localStorage.getItem(STORAGE_KEY);
    if (code && isCountryActive(code)) return code;
  } catch { /* ignore SSR / blocked storage */ }
  return DEFAULT_COUNTRY;
}

/** Sets the requested CSS variables on :root for live theme switching.
 * Overrides BOTH the new --primary/--accent vars AND the legacy --brand/--green
 * vars so existing components (which still reference --brand) pick up the
 * country palette without code changes.
 */
function applyTheme(theme) {
  if (typeof document === "undefined") return;
  const root = document.documentElement;
  root.style.setProperty("--primary", theme.primary);
  root.style.setProperty("--primary-soft", theme.primarySoft);
  root.style.setProperty("--accent-color", theme.accent);
  root.style.setProperty("--country-gradient", theme.gradient);
  root.style.setProperty("--brand-text-gradient", theme.textGradient || theme.gradient);
  root.style.setProperty("--country-bg", theme.background || "#ffffff");
  // Flag stripe colors — used by <CountryStripe /> below the navbar.
  const [s1, s2, s3] = theme.stripeColors || [theme.primary, "#ffffff", theme.accent];
  root.style.setProperty("--country-stripe-1", s1);
  root.style.setProperty("--country-stripe-2", s2);
  root.style.setProperty("--country-stripe-3", s3);
  // Legacy variable overrides — propagates to .btn-primary, .chip, etc.
  root.style.setProperty("--brand", theme.primary);
  root.style.setProperty("--brand-soft", theme.primarySoft);
  root.style.setProperty("--green", theme.accent);
  // Body background — for CI we keep the original CSS default (peach/mint).
  // For other countries we apply a tinted body via inline style so the country
  // is immediately recognisable.
  if (theme.bodyGradient && theme.bodyGradient !== "none") {
    document.body.style.backgroundColor = theme.background || "";
    document.body.style.backgroundImage = theme.bodyGradient;
    document.body.style.backgroundAttachment = "fixed";
    document.body.style.backgroundRepeat = "no-repeat";
    document.body.style.backgroundSize = "cover";
  } else {
    // Restore the CSS defaults (CI). Removing inline styles lets the index.css
    // peach→white→mint gradient take over again.
    document.body.style.backgroundColor = "";
    document.body.style.backgroundImage = "";
    document.body.style.backgroundAttachment = "";
    document.body.style.backgroundRepeat = "";
    document.body.style.backgroundSize = "";
  }
}

export function CountryProvider({ children }) {
  const [code, setCodeState] = useState(readStoredCountry);

  // Apply theme on every change
  useEffect(() => {
    applyTheme(getCountry(code).theme);
  }, [code]);

  const setCountry = useCallback((nextCode) => {
    if (!isCountryActive(nextCode)) return;
    try { localStorage.setItem(STORAGE_KEY, nextCode); } catch { /* ignore */ }
    setCodeState(nextCode);
  }, []);

  const country = getCountry(code);

  return (
    <CountryContext.Provider value={{ code, country, setCountry, all: COUNTRIES }}>
      {children}
    </CountryContext.Provider>
  );
}

export function useCountry() {
  const ctx = useContext(CountryContext);
  if (!ctx) throw new Error("useCountry must be used within CountryProvider");
  return ctx;
}
